const BASE_URL = "https://0xfire.com";

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "0xfire",
  url: BASE_URL,
  logo: `${BASE_URL}/0xfire.png`,
  description:
    "Crypto trading infrastructure company building algorithmic trading bots, real-time data APIs, and DeFi tools.",
  sameAs: [
    "https://github.com/0xfire-org",
    "https://x.com/0xconstantin",
    "https://discord.gg/cbtejQCpSh",
    "https://t.me/+VXGdd5c6HOkzZjVi",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    url: "https://calendly.com/0xconstantin/30min",
  },
  founder: {
    "@type": "Person",
    name: "0xconstantin",
  },
};

export const SAILFISH_PRODUCT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Sailfish",
  applicationCategory: "DeveloperApplication",
  operatingSystem: "Web",
  description:
    "Real-time trading data API for Solana, Ethereum, Binance Chain, Hyperliquid, Polymarket, and Kalshi. WebSocket streaming, historic REST API, and open-source TypeScript SDK.",
  offers: {
    "@type": "Offer",
    price: "75.00",
    priceCurrency: "USD",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      billingDuration: "P1M",
    },
    url: "https://cloud.solanavibestation.com/index.php?/cart/sailfish-api---streamer/",
  },
  provider: { "@type": "Organization", name: "0xfire", url: BASE_URL },
  featureList: [
    "Real-time WebSocket streaming (20-50ms latency)",
    "Historic REST API for trades and pool data",
    "Open-source TypeScript SDK",
    "99.9% uptime, 2.5TB daily bandwidth",
    "Raydium, PumpFun, PumpSwap, Meteora support",
  ],
};

export const SERVICES_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Crypto Trading Bot Development & Mentorship",
  provider: { "@type": "Organization", name: "0xfire", url: BASE_URL },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Trading Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Strategy Review & Audit",
          description:
            "One-on-one review call with risks & opportunities assessment and action plan.",
        },
        price: "499",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Build Your First Bot — Mentorship",
          description:
            "Weekly one-on-one mentoring sessions, tutorials library, and community access.",
        },
        price: "1599",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Strategy Backtesting",
          description:
            "Strategy implementation and backtesting on 3+ months of data with performance report.",
        },
        price: "2499",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Strategy Co-Development",
          description:
            "Full partnership: develop, backtest, deploy, host, and maintain your trading strategy.",
        },
        price: "1299",
        priceCurrency: "USD",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          billingDuration: "P1M",
        },
      },
    ],
  },
};

export const SERVICES_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What programming languages are used for crypto trading bots?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most bots are built in TypeScript (Node.js) for rapid development and Rust for latency-critical execution. Python is also used for backtesting and data analysis.",
      },
    },
    {
      "@type": "Question",
      name: "How long does it take to build a trading bot?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A basic bot can be built in 2-4 weeks with mentorship. A production-grade strategy with backtesting and deployment typically takes 4-8 weeks.",
      },
    },
    {
      "@type": "Question",
      name: "What chains and exchanges do you build trading bots for?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "0xfire builds trading bots across Solana DEXs (Raydium, PumpFun, Meteora), Ethereum, Binance Chain, Hyperliquid, and prediction markets (Polymarket, Kalshi).",
      },
    },
    {
      "@type": "Question",
      name: "Do I need coding experience for trading bot mentorship?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Basic programming knowledge helps, but the mentorship is designed to take beginners from zero to building their first bot step by step.",
      },
    },
  ],
};

export const WORKSHOP_EVENT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Event",
  name: "Don't Get Wrecked In Crypto",
  description:
    "A practical workshop for people who want to learn what to be afraid of in crypto trading. Covers risks, MEV bots, coin launches, financial instruments, and strategy building.",
  eventSchedule: {
    "@type": "Schedule",
    byDay: "https://schema.org/Sunday",
    startTime: "14:30",
    scheduleTimezone: "Europe/Berlin",
  },
  location: {
    "@type": "Place",
    name: "Berlin",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Berlin",
      addressCountry: "DE",
    },
  },
  offers: {
    "@type": "Offer",
    price: "65",
    priceCurrency: "EUR",
    availability: "https://schema.org/InStock",
  },
  organizer: { "@type": "Organization", name: "0xfire", url: BASE_URL },
};

export const WORKSHOP_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What will I learn at the crypto workshop?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The workshop covers trading risks, how coin launches work (memecoins, bonding curves), MEV bots and sandwich attacks, financial instruments (perpetuals, options, prediction markets), strategy building fundamentals, and live trading demos.",
      },
    },
    {
      "@type": "Question",
      name: "Do I need prior trading experience?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. The workshop is designed for people at all levels. It focuses on what to be afraid of rather than promising profits.",
      },
    },
    {
      "@type": "Question",
      name: "Where and when is the workshop?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The workshop is held weekly in Berlin on Sundays at 2:30 PM. It costs EUR 65.",
      },
    },
  ],
};

export const SAILFISH_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What protocols does Sailfish support on Solana?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sailfish supports Raydium (AMM, CPMM, CLMM, LaunchLab), PumpFun, PumpSwap, and Meteora (DYN, DYN2, DLMM) with both real-time streaming and historic API access for trades and pool initializations.",
      },
    },
    {
      "@type": "Question",
      name: "What is the difference between Trade and TradeRaw in Sailfish?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Trade is a human-readable format with decimal amounts and buy/sell classification based on PoolInfo. TradeRaw provides raw token amounts without PoolInfo dependency, giving you full control over interpretation.",
      },
    },
    {
      "@type": "Question",
      name: "How do I get started with the Sailfish SDK?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Install via npm install github:0xfire-org/sailfish-sdk, then use the typed callbacks (onTrade, onPoolInit, onTokenMint, etc.) to handle events. See the examples repo at github.com/0xfire-org/sailfish-examples.",
      },
    },
    {
      "@type": "Question",
      name: "What is Sailfish's latency compared to RPC nodes?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sailfish streams parsed trades at 20-50ms latency. Unlike raw RPC subscriptions, you receive structured trade data without needing to parse transaction logs, instruction data, or inner instructions yourself.",
      },
    },
    {
      "@type": "Question",
      name: "Can I use Sailfish for backtesting?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. The Historic API provides trade and pool data queryable by block range, token address, or pool address. You can build candlestick data, replay trades, and test strategies against real market data.",
      },
    },
    {
      "@type": "Question",
      name: "What chains does Sailfish support?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sailfish supports Solana, Ethereum, Binance Chain, Hyperliquid, Polymarket, and Kalshi. All chains are live with real-time streaming and historic API access.",
      },
    },
  ],
};

export const WRAPSOL_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is Wrapped SOL (wSOL)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Wrapped SOL (wSOL) is the SPL token version of native SOL, with mint address So111...112. All major Solana DeFi protocols use it for a uniform token interface.",
      },
    },
    {
      "@type": "Question",
      name: "Why do I need to wrap SOL?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "DEXs like Jupiter, Raydium, and Orca require wSOL for swaps, liquidity provision, and staking. Wrapping lets you use SOL like any other SPL token.",
      },
    },
    {
      "@type": "Question",
      name: "Is wrapping SOL safe?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes — it's a native Solana operation. Your SOL moves into an Associated Token Account that you own. Funds never leave your wallet. Unwrap anytime.",
      },
    },
    {
      "@type": "Question",
      name: "Does wrapping SOL cost anything?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Only the standard Solana transaction fee (~0.000005 SOL). If you don't have a wSOL account yet, the first wrap pays ~0.002 SOL rent deposit, which you get back when you unwrap.",
      },
    },
    {
      "@type": "Question",
      name: "Can I unwrap only part of my wSOL?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "This tool unwraps all wSOL by closing the token account. To keep some wSOL, re-wrap the desired amount after unwrapping.",
      },
    },
    {
      "@type": "Question",
      name: "Which wallets are supported?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Phantom, Solflare, Backpack, and any browser wallet that injects a standard Solana provider.",
      },
    },
  ],
};

export const WRAPSOL_APP_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Wrap & Unwrap SOL to wSOL",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  description:
    "Free tool to wrap and unwrap SOL to wSOL on Solana. Connect Phantom or Solflare wallet and convert between SOL and Wrapped SOL instantly. No fees, no sign-up.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  provider: { "@type": "Organization", name: "0xfire", url: BASE_URL },
};

export function getBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// Combine multiple schemas into a single graph for one script tag
export function combineSchemas(...schemas: object[]) {
  return {
    "@context": "https://schema.org",
    "@graph": schemas.map((s) => {
      const { "@context": _, ...rest } = s as Record<string, unknown>;
      return rest;
    }),
  };
}
